import { invalidateEventQueries } from '@/features/events/eventMutationCache';
import { getEventCampaignAttribution } from '@/features/events/eventCampaignAttribution';
import { eventDetailKeys, type EventDetailData } from '@/features/events/useEventDetail';
import { useAuth } from '@/lib/auth/auth';
import { APIError } from '@/lib/api/api';
import { useWritableMarketMutation } from '@/features/market/useWritableMarketMutation';
import type {
  CreateCommentRequest,
  CreateSavedEventRequest,
  RSVPRequest,
  RSVPVisibility,
} from '@/lib/api/apiTypes';
import { type CommentView, type DiscoveryEventDetail as RadiousEventDetail } from '@/lib/api/apiTypes';
import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { Platform } from 'react-native';

type DetailSnapshot = [QueryKey, EventDetailData | undefined][];

export function useEventDetailMutations() {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  const confirmGoing = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({
        eventID,
        visibility,
        source,
      }: {
        eventID: string;
        visibility: RSVPVisibility;
        source: RSVPRequest['source'];
      }) => {
        const attribution = await getEventCampaignAttribution(eventID);
        const payload: RSVPRequest = {
          status: 'going',
          visibility,
          source,
          surface: Platform.OS === 'web' ? 'web' : 'mobile_event_detail',
          ...attribution,
        };

        return request(`/events/${eventID}/rsvp`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      },
      onMutate: async ({ eventID }) => {
        await queryClient.cancelQueries({ queryKey: eventDetailKeys.byId(eventID) });
        const snapshot = snapshotDetailQueries(queryClient, eventID);
        updateDetailQueries(queryClient, eventID, (event) =>
          applyDetailRsvpMutation(event, 'going')
        );
        return { snapshot };
      },
      onError: (_error, variables, context) => {
        restoreDetailQueries(queryClient, context?.snapshot);
        void variables;
      },
      onSettled: async (_data, _error, variables) => {
        await invalidateEventQueries(queryClient, variables.eventID);
      },
    })
  );

  const removeRsvp = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({ eventID }: { eventID: string }) => {
        const surface = Platform.OS === 'web' ? 'web' : 'mobile_event_detail';
        return request(`/events/${eventID}/rsvp?source=detail&surface=${surface}`, {
          method: 'DELETE',
        });
      },
      onMutate: async ({ eventID }) => {
        await queryClient.cancelQueries({ queryKey: eventDetailKeys.byId(eventID) });
        const snapshot = snapshotDetailQueries(queryClient, eventID);
        updateDetailQueries(queryClient, eventID, applyDetailRsvpRemoval);
        return { snapshot };
      },
      onError: (_error, _variables, context) =>
        restoreDetailQueries(queryClient, context?.snapshot),
      onSettled: async (_data, _error, variables) => {
        await invalidateEventQueries(queryClient, variables.eventID);
      },
    })
  );

  const toggleSaved = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({ event }: { event: RadiousEventDetail }) => {
        if (event.is_saved) {
          await request(`/saved-events/${event.id}`, { method: 'DELETE' });
          return false;
        }

        await request('/saved-events', {
          method: 'POST',
          body: JSON.stringify({
            event_id: event.id,
            ...(await getEventCampaignAttribution(event.id)),
          } satisfies CreateSavedEventRequest),
        });
        return true;
      },
      onMutate: async ({ event }) => {
        await queryClient.cancelQueries({ queryKey: eventDetailKeys.byId(event.id) });
        const snapshot = snapshotDetailQueries(queryClient, event.id);
        updateDetailQueries(queryClient, event.id, (current) =>
          applyDetailSaveMutation(current, !event.is_saved)
        );
        return { snapshot };
      },
      onError: (_error, _variables, context) =>
        restoreDetailQueries(queryClient, context?.snapshot),
      onSettled: async (_data, _error, variables) => {
        await invalidateEventQueries(queryClient, variables.event.id);
      },
    })
  );

  const toggleFollow = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({
        eventID,
        username,
        isFollowing,
      }: {
        eventID: string;
        username: string;
        isFollowing: boolean;
      }) => {
        if (isFollowing) {
          await request(`/profiles/${username}/follow`, { method: 'DELETE' });
          return false;
        }

        await request(`/profiles/${username}/follow`, { method: 'POST' });
        return true;
      },
      onMutate: async ({ eventID, isFollowing }) => {
        await queryClient.cancelQueries({ queryKey: eventDetailKeys.byId(eventID) });
        const snapshot = snapshotDetailQueries(queryClient, eventID);
        updateDetailQueries(queryClient, eventID, (event) => ({
          ...event,
          host: {
            ...event.host,
            is_following: !isFollowing,
          },
        }));
        return { snapshot };
      },
      onError: (_error, _variables, context) =>
        restoreDetailQueries(queryClient, context?.snapshot),
      onSettled: async (_data, _error, variables) => {
        await invalidateEventQueries(queryClient, variables.eventID);
      },
    })
  );

  const submitComment = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({ eventID, body }: { eventID: string; body: string }) => {
        try {
          return await request<CommentView>(`/events/${eventID}/comments`, {
            method: 'POST',
            body: JSON.stringify({ body } satisfies CreateCommentRequest),
          });
        } catch (error) {
          if (error instanceof APIError && error.status === 422) {
            throw new Error('That comment needs a quick edit before it can be posted.');
          }
          if (error instanceof APIError && error.status === 503) {
            throw new Error('Safety checks are busy right now. Try again in a minute.');
          }
          throw error;
        }
      },
      onSuccess: (comment, variables) => {
        queryClient.setQueriesData<EventDetailData>(
          { queryKey: eventDetailKeys.byId(variables.eventID) },
          (data) =>
            data
              ? {
                  ...data,
                  event: {
                    ...data.event,
                    comment_count: data.event.comment_count + 1,
                  },
                  comments: [...data.comments, comment],
                }
              : data
        );
      },
      onSettled: async (_data, _error, variables) => {
        await queryClient.invalidateQueries({ queryKey: eventDetailKeys.byId(variables.eventID) });
      },
    })
  );

  const deleteComment = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({ eventID, commentID }: { eventID: string; commentID: string }) => {
        return request(`/events/${eventID}/comments/${commentID}`, { method: 'DELETE' });
      },
      onSuccess: (_data, variables) => {
        queryClient.setQueriesData<EventDetailData>(
          { queryKey: eventDetailKeys.byId(variables.eventID) },
          (data) =>
            data
              ? {
                  ...data,
                  event: {
                    ...data.event,
                    comment_count: Math.max(0, data.event.comment_count - 1),
                  },
                  comments: data.comments.filter((comment) => comment.id !== variables.commentID),
                }
              : data
        );
      },
      onSettled: async (_data, _error, variables) => {
        await queryClient.invalidateQueries({ queryKey: eventDetailKeys.byId(variables.eventID) });
      },
    })
  );

  const hideComment = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({ eventID, commentID }: { eventID: string; commentID: string }) => {
        return request(`/events/${eventID}/comments/${commentID}/hide`, { method: 'POST' });
      },
      onSuccess: (_data, variables) => {
        queryClient.setQueriesData<EventDetailData>(
          { queryKey: eventDetailKeys.byId(variables.eventID) },
          (data) =>
            data
              ? {
                  ...data,
                  comments: data.comments.map((c) =>
                    c.id === variables.commentID ? { ...c, is_hidden: true, hidden_by_me: true } : c
                  ),
                }
              : data
        );
      },
      onSettled: async (_data, _error, variables) => {
        await queryClient.invalidateQueries({ queryKey: eventDetailKeys.byId(variables.eventID) });
      },
    })
  );

  const unhideComment = useWritableMarketMutation(
    useMutation({
      mutationFn: async ({ eventID, commentID }: { eventID: string; commentID: string }) => {
        return request(`/events/${eventID}/comments/${commentID}/hide`, { method: 'DELETE' });
      },
      onSuccess: (_data, variables) => {
        queryClient.setQueriesData<EventDetailData>(
          { queryKey: eventDetailKeys.byId(variables.eventID) },
          (data) =>
            data
              ? {
                  ...data,
                  comments: data.comments.map((c) =>
                    c.id === variables.commentID
                      ? { ...c, is_hidden: false, hidden_by_me: false }
                      : c
                  ),
                }
              : data
        );
      },
      onSettled: async (_data, _error, variables) => {
        await queryClient.invalidateQueries({ queryKey: eventDetailKeys.byId(variables.eventID) });
      },
    })
  );

  return {
    confirmGoing,
    removeRsvp,
    toggleSaved,
    toggleFollow,
    submitComment,
    deleteComment,
    hideComment,
    unhideComment,
  };
}

function snapshotDetailQueries(queryClient: QueryClient, eventID: string): DetailSnapshot {
  return queryClient.getQueriesData<EventDetailData>({
    queryKey: eventDetailKeys.byId(eventID),
  });
}

function restoreDetailQueries(queryClient: QueryClient, snapshot: DetailSnapshot | undefined) {
  if (!snapshot) {
    return;
  }

  for (const [queryKey, data] of snapshot) {
    queryClient.setQueryData(queryKey, data);
  }
}

function updateDetailQueries(
  queryClient: QueryClient,
  eventID: string,
  updateEvent: (event: RadiousEventDetail) => RadiousEventDetail
) {
  queryClient.setQueriesData<EventDetailData>(
    { queryKey: eventDetailKeys.byId(eventID) },
    (data) =>
      data
        ? {
            ...data,
            event: updateEvent(data.event),
          }
        : data
  );
}

function applyDetailRsvpMutation(event: RadiousEventDetail, status: 'going') {
  const wasGoing = event.viewer_rsvp_status === 'going';

  return {
    ...event,
    viewer_rsvp_status: status,
    rsvp_count: wasGoing ? event.rsvp_count : event.rsvp_count + 1,
  };
}

function applyDetailSaveMutation(event: RadiousEventDetail, isSaved: boolean) {
  return {
    ...event,
    is_saved: isSaved,
  };
}

function applyDetailRsvpRemoval(event: RadiousEventDetail) {
  return {
    ...event,
    viewer_rsvp_status: '',
    rsvp_count:
      event.viewer_rsvp_status === 'going' ? Math.max(0, event.rsvp_count - 1) : event.rsvp_count,
  };
}
